import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'

import { useAppRouter as useRouter } from '@/shared/hooks/useAppRouter'

import { useSession, signIn, signOut, getSession } from '@/shared/services/session/compat'

import { useRenderContext } from '@/shared/contexts/RenderContext'
import type { PartnerParams, PartnerAuthPayload } from '@/types/partner'

import { findMatchingPartner } from './registry'
import { pickEmail, pickPartnerId, pickSlug } from './utils'

type PartnerState = {
  id?: string
  pluginName?: string
  params?: PartnerParams
  active: boolean
  activating: boolean
  error?: string
}

const PartnerCtx = createContext<PartnerState>({
  active: false,
  activating: false
})

export function PartnerProvider({ children }: { children: React.ReactNode }) {
  const router = useRouter()
  const { digitalProductId } = router.query
  const { context: renderContext, ready } = useRenderContext()
  const { data: session, status } = useSession()

  const [state, setState] = useState<PartnerState>({
    active: false,
    activating: false
  })
  const [params, setParams] = useState<PartnerParams | null>(null)
  const [payload, setPayload] = useState<PartnerAuthPayload | null>(null)

  const runId = useRef(0)

  // Chave reativa: users.email pode ser sintético (doity/brasiljunior), então
  // a comparação de identidade usa identities[].meta.email, não session.user.email.
  const sessionPartnerIdentitiesKey = (session?.user?.identities || [])
    .map(identity => {
      const metaEmail = identity.meta?.email
      return `${identity.provider}:${
        typeof metaEmail === 'string' ? metaEmail.toLowerCase() : ''
      }`
    })
    .join('|')

  const queryParams = useMemo(() => {
    if (typeof window === 'undefined') return {}
    const url = new URL(window.location.href)
    return Object.fromEntries(url.searchParams)
  }, [router.asPath])

  useEffect(() => {
    if (
      !ready ||
      typeof window === 'undefined' ||
      !digitalProductId ||
      status === 'loading'
    )
      return () => {}

    const currentRun = ++runId.current

    ;(async () => {
      try {
        const referer = document.referrer || undefined
        const ua = navigator.userAgent || undefined

        const candidates: Array<{
          source: PartnerParams['source']
          raw: Record<string, string>
        }> = []

        if (queryParams['partner'] || queryParams['partner_id']) {
          candidates.push({ source: 'query', raw: queryParams })
        }
        if (referer) candidates.push({ source: 'referrer', raw: { referer } })
        candidates.push({ source: 'ua', raw: { ua: ua || '' } })

        for (const c of candidates) {
          if (currentRun !== runId.current) return // corrida cancelada

          const slug = pickSlug(c.raw, digitalProductId as string)
          const partnerId = pickPartnerId(c.raw)
          const email = pickEmail(c.raw)

          const p: PartnerParams = {
            source: c.source,
            raw: c.raw,
            renderContext,
            referer,
            ua,
            digitalProductId: digitalProductId as string,
            slug,
            partnerId,
            email
          }

          const plugin = findMatchingPartner(p)
          if (!plugin) continue

          setParams(p)
          setState(s => ({ ...s, activating: true, error: undefined }))

          const pl = plugin.getAuthPayload?.(p) ?? null
          setPayload(pl)

          if (pl) {
            const targetEmail = p.email?.toLowerCase()
            const identityEmail = session?.user?.identities?.find(
              identity => identity.provider === pl.provider
            )?.meta?.email
            const sameEmail =
              !!targetEmail &&
              typeof identityEmail === 'string' &&
              identityEmail.toLowerCase() === targetEmail
            const authenticated = status == 'authenticated'

            if (!authenticated || !sameEmail) {
              if (!sameEmail) {
                try {
                  await signOut({ redirect: false })
                } catch {}
              }
              await signIn('partner-provider', {
                token: pl.token,
                provider: pl.provider
              })
            }
          }

          if (plugin.onActivated) {
            if (currentRun !== runId.current) return
            await plugin.onActivated()
          }

          if (currentRun !== runId.current) return
          setState({
            id: plugin.id,
            pluginName: plugin.name,
            params: p,
            active: true,
            activating: false,
            error: undefined
          })
          return
        }

        if (currentRun === runId.current) {
          setState(s => ({ ...s, active: false, activating: false }))
          setParams(null)
          setPayload(null)
        }
      } catch (err: any) {
        if (currentRun === runId.current) {
          setState({ active: false, activating: false, error: String(err) })
        }
      }
    })()

    return () => {
      runId.current++
    }
  }, [
    ready,
    renderContext,
    router.asPath,
    digitalProductId,
    sessionPartnerIdentitiesKey,
    status
  ])

  useEffect(() => {
    if (typeof window === 'undefined') return () => {}

    const onStorage = (e: StorageEvent) => {
      if (e.key && e.key.includes('nextauth')) {
        runId.current++
      }
    }

    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  return <PartnerCtx.Provider value={state}>{children}</PartnerCtx.Provider>
}

function sleep(ms: number) {
  return new Promise(r => setTimeout(r, ms))
}

export function usePartner() {
  return useContext(PartnerCtx)
}
